import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * "Danger zone" section — lets a user delete their own account (RGPD right to erasure).
 * Requires typing DELETE + password? We keep it simple: confirm dialog + optional
 * commercial reason. On success, logs out and redirects to landing.
 */
export default function DangerZone() {
    const { logout } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);

    const canDelete = confirmText.trim().toUpperCase() === "SUPPRIMER";

    const doDelete = async () => {
        if (!canDelete) return;
        setDeleting(true);
        try {
            await api.delete("/me");
            toast.success("Votre compte a été supprimé.");
            if (logout) logout();
            nav("/");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur lors de la suppression.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section className="sd-card p-6 border-2 border-red-200 bg-red-50/40" data-testid="danger-zone">
            <div className="overline text-red-600 mb-2">Zone de danger</div>
            <h2 className="font-display text-xl mb-2">Supprimer mon compte</h2>
            <p className="text-sm text-slate-600 mb-4">
                Vous pouvez à tout moment demander la suppression de votre compte
                Renforteo, conformément à votre <b>droit à l'oubli (RGPD)</b>.
                Cette action est définitive :
            </p>
            <ul className="text-sm text-slate-600 mb-4 space-y-1 list-disc list-inside">
                <li>Votre profil, votre photo et vos documents seront effacés</li>
                <li>Vos candidatures en cours seront supprimées</li>
                <li>Vos évaluations émises seront supprimées</li>
                <li>Vous ne pourrez plus vous connecter avec cet e-mail</li>
            </ul>
            <p className="text-xs text-slate-500 mb-4 italic">
                Note légale : les factures et missions déjà réalisées restent
                archivées de manière anonymisée pour la conformité comptable
                (obligation française de conservation 10 ans).
            </p>

            {!open ? (
                <button
                    onClick={() => setOpen(true)}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition"
                    data-testid="danger-open-btn"
                >
                    Supprimer mon compte
                </button>
            ) : (
                <div className="border border-red-300 rounded-xl p-4 bg-white space-y-3">
                    <div className="text-sm font-semibold text-red-700">
                        Tapez <span className="font-mono bg-red-100 px-2 py-0.5 rounded">SUPPRIMER</span> pour confirmer.
                    </div>
                    <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border-2 border-red-200 focus:border-red-500 outline-none text-sm"
                        placeholder="SUPPRIMER"
                        data-testid="danger-confirm-input"
                    />
                    <div className="flex gap-2">
                        <button
                            onClick={() => { setOpen(false); setConfirmText(""); }}
                            className="btn-ghost-navy text-sm flex-1"
                            data-testid="danger-cancel-btn"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={doDelete}
                            disabled={!canDelete || deleting}
                            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition flex-1"
                            data-testid="danger-confirm-btn"
                        >
                            {deleting ? "Suppression..." : "Confirmer la suppression"}
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
